function normalizeMessages(messages) {
    if (Array.isArray(messages)) {
        return messages.filter((message) => typeof message === 'string' && message.trim() !== '');
    }

    if (typeof messages === 'string' && messages.trim() !== '') {
        return [messages];
    }

    return [];
}

function firstError(errors, field) {
    if (!errors || typeof errors !== 'object') {
        return null;
    }

    const messages = normalizeMessages(errors[field]);

    return messages.length > 0 ? messages[0] : null;
}

export function registerAdminTagSelectors(Alpine) {
    Alpine.data('contentAdminGroupedTagsSelector', () => ({
        groups: [],
        selected: [],
        activeGroupId: '',
        activeTagId: '',

        init() {
            this.groups = this.readJsonRef('groupsData');
            this.selected = this.normalizeSelected(this.readJsonRef('initialSelectedData'));

            if (this.groups.length > 0) {
                this.activeGroupId = String(this.groups[0].id);
                this.onGroupChange();
            }
        },

        readJsonRef(refName) {
            const raw = this.$refs[refName]?.textContent || '[]';

            try {
                const parsed = JSON.parse(raw);

                return Array.isArray(parsed) ? parsed : [];
            } catch {
                return [];
            }
        },

        normalizeSelected(items) {
            const deduped = new Map();

            items.forEach((item) => {
                const id = Number(item.id || 0);

                if (id <= 0 || deduped.has(id)) {
                    return;
                }

                deduped.set(id, {
                    id,
                    name: String(item.name || '').trim(),
                    slug: String(item.slug || '').trim(),
                    group_id: Number(item.group_id || 0),
                    group_name: String(item.group_name || '').trim(),
                });
            });

            return Array.from(deduped.values());
        },

        selectedTagIds() {
            return this.selected.map((item) => Number(item.id));
        },

        serializedTagIds() {
            return JSON.stringify(this.selectedTagIds());
        },

        currentGroup() {
            const groupId = Number(this.activeGroupId || 0);

            if (groupId <= 0) {
                return null;
            }

            return this.groups.find((group) => Number(group.id) === groupId) || null;
        },

        availableTags() {
            const group = this.currentGroup();

            if (!group || !Array.isArray(group.tags)) {
                return [];
            }

            const selectedIds = this.selectedTagIds();

            return group.tags
                .map((tag) => ({
                    id: Number(tag.id || 0),
                    name: String(tag.name || '').trim(),
                    slug: String(tag.slug || '').trim(),
                }))
                .filter((tag) => tag.id > 0 && tag.name !== '')
                .filter((tag) => !selectedIds.includes(tag.id));
        },

        onGroupChange() {
            const firstAvailable = this.availableTags()[0] || null;

            this.activeTagId = firstAvailable ? String(firstAvailable.id) : '';
        },

        addActiveTag() {
            const tagId = Number(this.activeTagId || 0);

            if (tagId <= 0) {
                return;
            }

            const group = this.currentGroup();

            if (!group) {
                return;
            }

            const match = this.availableTags().find((tag) => tag.id === tagId);

            if (!match) {
                this.onGroupChange();
                return;
            }

            this.selected.push({
                id: match.id,
                name: match.name,
                slug: match.slug,
                group_id: Number(group.id),
                group_name: String(group.name || '').trim(),
            });

            this.onGroupChange();
        },

        removeSelected(tagId) {
            const numeric = Number(tagId || 0);

            if (numeric <= 0) {
                return;
            }

            this.selected = this.selected.filter((item) => item.id !== numeric);
            this.onGroupChange();
        },

        firstErrorFor(errors, field) {
            return firstError(errors, field);
        },
    }));

    Alpine.data('taxonomyAdminTagSelector', (config = {}) => ({
        searchUrl: config.searchUrl ?? '',
        query: '',
        candidates: [],
        selectedExisting: [],
        selectedNew: [],
        selected: [],
        isSearching: false,
        searchTimer: null,
        searchAbortController: null,
        currentSearchToken: 0,

        init() {
            this.selectedExisting = this.normalizeExisting(config.initialExisting || []);
            this.selectedNew = this.normalizeNew(config.initialNew || []);
            this.rebuildSelected();

            window.addEventListener('content-form:synced', (event) => {
                const tags = event.detail?.tags;

                if (!Array.isArray(tags)) {
                    return;
                }

                this.selectedExisting = this.normalizeExisting(tags);
                this.selectedNew = [];
                this.rebuildSelected();
            });
        },

        normalizeExisting(items) {
            const deduped = new Map();

            items.forEach((item) => {
                const id = Number(item.id || 0);
                const name = String(item.name || '').trim();
                const slug = this.slugify(item.slug || name);

                if (id <= 0 || name === '' || slug === '' || deduped.has(slug)) {
                    return;
                }

                deduped.set(slug, {
                    id,
                    name,
                    slug,
                });
            });

            return Array.from(deduped.values());
        },

        normalizeNew(items) {
            const deduped = new Map();

            items.forEach((item) => {
                const name = String(item.name || '').trim();
                const slug = this.slugify(item.slug || name);

                if (name === '' || slug === '' || deduped.has(slug)) {
                    return;
                }

                deduped.set(slug, {
                    name,
                    slug,
                    blurb: item.blurb ?? null,
                });
            });

            return Array.from(deduped.values()).filter((item) => !this.hasExistingSlug(item.slug));
        },

        rebuildSelected() {
            const existing = this.selectedExisting.map((item) => ({
                key: `existing-${item.id}`,
                type: 'existing',
                id: item.id,
                slug: item.slug,
                name: item.name,
            }));

            const created = this.selectedNew.map((item) => ({
                key: `new-${item.slug}`,
                type: 'new',
                slug: item.slug,
                name: item.name,
            }));

            this.selected = [...existing, ...created];
        },

        hasExistingSlug(slug) {
            return this.selectedExisting.some((item) => item.slug === slug);
        },

        hasNewSlug(slug) {
            return this.selectedNew.some((item) => item.slug === slug);
        },

        hasSlug(slug) {
            return this.hasExistingSlug(slug) || this.hasNewSlug(slug);
        },

        slugify(value) {
            const input = String(value || '').trim().toLowerCase();

            if (input === '') {
                return '';
            }

            return input
                .normalize('NFKD')
                .replace(/[\u0300-\u036f]/g, '')
                .replace(/[^a-z0-9]+/g, '-')
                .replace(/^-+|-+$/g, '');
        },

        clearInputAndFocus() {
            this.query = '';
            this.candidates = [];
            this.focusInput();
        },

        focusInput() {
            if (this.$refs.tagInput) {
                this.$nextTick(() => this.$refs.tagInput.focus());
            }
        },

        serializedTagIds() {
            return JSON.stringify(this.selectedExisting.map((item) => item.id));
        },

        serializedNewTags() {
            return JSON.stringify(
                this.selectedNew.map((item) => ({
                    name: item.name,
                    blurb: item.blurb ?? null,
                })),
            );
        },

        removeSelected(item) {
            if (item.type === 'existing') {
                this.selectedExisting = this.selectedExisting.filter((existing) => existing.id !== item.id);
            } else if (item.type === 'new') {
                this.selectedNew = this.selectedNew.filter((created) => created.slug !== item.slug);
            }

            this.rebuildSelected();
            this.focusInput();
        },

        onInput() {
            const term = this.query.trim();

            if (term.length < 3) {
                this.cancelSearch();
                this.candidates = [];
                this.isSearching = false;
                return;
            }

            if (this.searchTimer) {
                clearTimeout(this.searchTimer);
            }

            this.searchTimer = setTimeout(() => {
                this.searchCandidates(term);
            }, 180);
        },

        cancelSearch() {
            if (this.searchTimer) {
                clearTimeout(this.searchTimer);
                this.searchTimer = null;
            }

            if (this.searchAbortController) {
                this.searchAbortController.abort();
                this.searchAbortController = null;
            }
        },

        async searchCandidates(term) {
            this.cancelSearch();
            this.searchAbortController = new AbortController();
            this.currentSearchToken += 1;
            const searchToken = this.currentSearchToken;
            this.isSearching = true;

            try {
                const url = new URL(this.searchUrl, window.location.origin);
                url.searchParams.set('q', term);

                const response = await fetch(url.toString(), {
                    method: 'GET',
                    headers: {
                        Accept: 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                    credentials: 'same-origin',
                    signal: this.searchAbortController.signal,
                });

                if (searchToken !== this.currentSearchToken) {
                    return;
                }

                if (!response.ok) {
                    this.candidates = [];
                    return;
                }

                const payload = await response.json();
                const data = Array.isArray(payload?.data) ? payload.data : [];

                this.candidates = data
                    .map((item) => ({
                        id: Number(item.id || 0),
                        name: String(item.name || '').trim(),
                        slug: this.slugify(item.slug || item.name),
                    }))
                    .filter((item) => item.id > 0 && item.name !== '' && item.slug !== '')
                    .filter((item) => !this.hasSlug(item.slug));
            } catch (error) {
                if (error?.name !== 'AbortError') {
                    this.candidates = [];
                }
            } finally {
                if (searchToken === this.currentSearchToken) {
                    this.isSearching = false;
                }

                this.searchAbortController = null;
            }
        },

        commitInput() {
            const name = this.query.trim();
            const slug = this.slugify(name);

            if (name === '' || slug === '') {
                this.clearInputAndFocus();
                return;
            }

            const candidate = this.candidates.find((item) => item.slug === slug || item.name.toLowerCase() === name.toLowerCase());

            if (candidate) {
                this.selectCandidate(candidate);
                return;
            }

            if (!this.hasSlug(slug)) {
                this.selectedNew.push({
                    name,
                    slug,
                    blurb: null,
                });
            }

            this.rebuildSelected();
            this.clearInputAndFocus();
        },

        selectCandidate(candidate) {
            const slug = this.slugify(candidate.slug || candidate.name);
            const id = Number(candidate.id || 0);
            const name = String(candidate.name || '').trim();

            if (id <= 0 || name === '' || slug === '') {
                this.clearInputAndFocus();
                return;
            }

            if (!this.hasExistingSlug(slug)) {
                this.selectedNew = this.selectedNew.filter((item) => item.slug !== slug);
                this.selectedExisting.push({
                    id,
                    name,
                    slug,
                });
            }

            this.rebuildSelected();
            this.clearInputAndFocus();
        },

        firstErrorFor(errors, field) {
            return firstError(errors, field);
        },
    }));
}
